<template>
    <div class="af3-shotcoder-filter-bar-numbers-container">
        <label :for="`af3-shotcoder-filter-bar-numbers-${type}`">{{ type }}</label>

        <div class="af3-shotcoder-filter-bar-numbers-container-buttons">
            <button :disabled="selectedValue <= (min - 1)" @click="handleCLickSubtract">-</button>
            <span>{{ handleStringAny() }}</span>
            <button :disabled="isMaxValue" @click="handleCLickAdd">+</button>
        </div>
    </div>
</template>
  
<script>

import { defineComponent, ref, watch, onMounted, computed } from 'vue';


export default defineComponent({
    components: { },
   
    props: {
        min: {
            type: Number,
            required: true
        },
        max: {
            type: Number,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        value: {
            type: Number,
            required: false,
            default: null
        },
        available_values: {
            type: Array,
            required: true
        }
    },
    
    emits: ['update:value'],
    
    setup(props, { emit }) {
        // Initialize with value if provided, otherwise use min-1 (representing "Any")
        const selectedValue = ref(props.value !== null ? Number(props.value) : props.min - 1);

        // Sort available values to ensure they're in order and convert to numbers
        const sortedAvailableValues = computed(() => {
            // Convert all values to numbers to ensure correct comparisons
            const numberedValues = props.available_values.map(val => Number(val));
            return [...numberedValues].sort((a, b) => a - b);
        });

        // Computed property to check if we're at the maximum value
        const isMaxValue = computed(() => {
            // If no available values, disable button
            if (sortedAvailableValues.value.length === 0) {
                return true;
            }
            
            if (selectedValue.value === props.min - 1) {
                // If at "Any", button should be enabled if there are available values
                return false;
            }
            
            // Find current index
            const index = sortedAvailableValues.value.indexOf(selectedValue.value);
            
            // If value not found or is the last one, disable button
            return index === -1 || index === sortedAvailableValues.value.length - 1;
        });

        const handleCLickAdd = () => {
            if (sortedAvailableValues.value.length === 0) {
                // No values to navigate to
                return;
            }
            
            if (selectedValue.value === props.min - 1) {
                // If currently at "Any", set to the first available value
                selectedValue.value = sortedAvailableValues.value[0];
            } else {
                // Find current index in available values
                const currentIndex = sortedAvailableValues.value.indexOf(selectedValue.value);
                
                // If not found in array, start with first value
                if (currentIndex === -1) {
                    selectedValue.value = sortedAvailableValues.value[0];
                } else if (currentIndex < sortedAvailableValues.value.length - 1) {
                    // Move to next available value
                    selectedValue.value = sortedAvailableValues.value[currentIndex + 1];
                }
            }
            
            emit('update:value', selectedValue.value);
        }

        const handleCLickSubtract = () => {
            // Don't go below min-1
            if (selectedValue.value <= props.min - 1) {
                selectedValue.value = props.min - 1;
                return;
            }
            
            // Find current index in available values
            const currentIndex = sortedAvailableValues.value.indexOf(selectedValue.value);
            
            // If not found in array or it's the first value, go to "Any"
            if (currentIndex === -1 || currentIndex === 0) {
                selectedValue.value = props.min - 1;
            } else if (currentIndex > 0) {
                // Move to previous available value
                selectedValue.value = sortedAvailableValues.value[currentIndex - 1];
            }
            
            emit('update:value', selectedValue.value);
        }

        const handleStringAny = () => {
            if(selectedValue.value <= props.min - 1) {
                return 'Any';
            }
            
            if(props.type === 'bedrooms'){
                if(selectedValue.value === 0){
                    return 'Studio';
                }
                return selectedValue.value;
            }
            
            return selectedValue.value;
        }
        
        // Watch for changes in value prop
        watch(() => props.value, (newVal) => {
            if (newVal !== null) {
                selectedValue.value = Number(newVal);
            } else {
                selectedValue.value = props.min - 1; // Default to min-1 (Any) if value is null
            }
        });
        
        // Watch for changes in min/max range props
        watch(() => props.min, (newVal) => {
            if (props.value === null) {
                selectedValue.value = Number(newVal) - 1; // Reset to "Any" state
            }
        });
        
        // Watch for changes in selected value to emit updates
        watch(selectedValue, (newVal) => {
            // Only emit if it's not the default "Any" value
            if (newVal !== props.min - 1) {
                emit('update:value', newVal);
            } else {
                // For "Any" value, emit null
                emit('update:value', null);
            }
        });
        
        // Initialize component
        onMounted(() => {
            // Ensure initial values are valid
            if (selectedValue.value > props.max) {
                selectedValue.value = props.max;
            }
            
            // Force a refresh of the values to ensure they're properly initialized
            if (props.value === null) {
                selectedValue.value = props.min - 1;
            }
            
            // Emit initial value if not "Any"
            if (selectedValue.value !== props.min - 1) {
                emit('update:value', selectedValue.value);
            }
        });
          
        return {
          selectedValue,
          handleCLickAdd,
          handleCLickSubtract,
          handleStringAny,
          isMaxValue,
          sortedAvailableValues
        };
    },
});
</script>